import { useEffect } from 'react';

import { useTeam } from '../context/TeamProvider';
import { useTeamQuery } from '../graphql/team/queryTeam';
import useOrganizationStore from '../store/organization';
import { tenantBrandForTeam } from './tenant';

/**
 * Publishes the current team's tenant brand into `useOrganizationStore`. Mount once inside the
 * team context, beside `usePlanSync` — `PrivateApp` and `PrivateWizard`.
 *
 * Reads `TEAM_QUERY` with the DEFAULT options, the same ones `Sidebar` and `useCustomTheme` use,
 * so Apollo shares one observable instead of firing a second request. That document opts into
 * `RETRYABLE_CONTEXT`: without it a single failed fetch pinned the brand at `null` for the whole
 * session, since nothing here polls or refetches.
 *
 * Derivation happens during render via the pure `tenantBrandForTeam`, not in `onCompleted` —
 * that does not fire for cache-only reads, which is exactly what returning to a visited team
 * produces. Left in `onCompleted`, the brand stayed on whatever the last team that took the
 * network path had, so a Nexoya team could render as white-label (and worse, the reverse).
 *
 * There is deliberately NO fail-open on `error`, which is where this parts company with
 * `usePlanSync`: a failed plan lookup must not lock a paying team out, but a failed tenant
 * lookup guessing a brand is how the vendor's name reaches an agency's own client. Unknown
 * stays unknown.
 */
export function useTenantSync(): void {
  const { teamId } = useTeam();
  const setTenantBrand = useOrganizationStore((s) => s.setTenantBrand);
  const { data, error } = useTeamQuery();

  // The error wins over any payload that survived it, exactly as it does in `usePlanSync` — but
  // to `null` rather than to a default. Under `errorPolicy: 'all'` a field-level error on
  // `organization` arrives as `organization: null`, which is byte-identical to a genuine
  // white-label answer; branding a Nexoya customer as somebody else on a transient failure is
  // the bug this file exists to remove.
  const tenantBrand = error ? null : tenantBrandForTeam(teamId, data?.team);

  useEffect(() => {
    setTenantBrand(tenantBrand);
  }, [tenantBrand, setTenantBrand]);
}
